/**
 * @module @verba/chat-sdk/messenger
 * Secure bi-directional postMessage bridge between the host page and the widget iframe.
 */

import type { MessageHandler, MessageType, MessengerOptions, PostMessagePayload } from './types.ts'

/**
 * `Messenger` abstracts away the raw `window.postMessage` / `addEventListener`
 * API and enforces:
 *  - Typed send API.
 */
export class Messenger {
    private readonly targetWindow: Window
    private readonly targetOrigin: string
    private readonly allowedOrigin: string

    /** Map of message-type → set of handlers registered via `.on()`. */
    private readonly handlers = new Map<MessageType, Set<MessageHandler>>()

    /** Bound reference kept so we can remove the exact same listener in `destroy()`. */
    private readonly boundListener: (event: MessageEvent) => void

    constructor(options: MessengerOptions) {
        this.targetWindow = options.targetWindow
        this.targetOrigin = options.targetOrigin
        this.allowedOrigin = options.allowedOrigin

        this.boundListener = this.handleMessage.bind(this)
        window.addEventListener('message', this.boundListener)
    }

    // ─── Public API ─────────────────────────────────────────────────────────────

    /**
     * Send a typed message **to** the widget iframe.
     *
     * @param type  - One of the protocol's `MessageType` values.
     * @param data  - Arbitrary payload; typed by the caller.
     */
    send<T>(type: MessageType, data: T): void {
        const payload: PostMessagePayload<T> = {
            source: 'verba-chat-sdk',
            type,
            data,
        }
        this.targetWindow.postMessage(payload, this.targetOrigin)
    }

    /**
     * Subscribe to a specific message type **from** the widget.
     * Returns an unsubscribe function for convenience.
     *
     * @param type    - The `MessageType` to listen for.
     * @param handler - Callback invoked with the `data` field of the payload.
     */
    on<T>(type: MessageType, handler: MessageHandler<T>): () => void {
        if (!this.handlers.has(type)) {
            this.handlers.set(type, new Set())
        }
        const set = this.handlers.get(type)!
        set.add(handler as MessageHandler)

        return () => {
            set.delete(handler as MessageHandler)
        }
    }

    /**
     * Remove all registered handlers and the window-level `message` listener.
     * Must be called during SDK teardown to prevent memory leaks.
     */
    destroy(): void {
        window.removeEventListener('message', this.boundListener)
        this.handlers.clear()
    }

    // ─── Private ────────────────────────────────────────────────────────────────

    private handleMessage(event: MessageEvent): void {
        // Enforce origin allowlist
        if (event.origin !== this.allowedOrigin) return

        // Enforce namespace guard
        const payload = event.data as PostMessagePayload
        if (
            typeof payload !== 'object' ||
            payload === null ||
            payload.source !== 'verba-chat-sdk'
        ) {
            return
        }

        // Dispatch to registered handlers
        const set = this.handlers.get(payload.type)
        if (!set) return

        for (const handler of set) {
            try {
                handler(payload.data)
            } catch (err) {
                console.error(`[VerbaChat] Handler for "${payload.type}" threw:`, err)
            }
        }
    }
}
